import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpenIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import manual from '../data/manual/MANUAL-USUARIO.md.js';

const foldText = (value = '') => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase('es');

const slugify = (value) => foldText(value)
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

const safeUrl = (value) => {
  const url = value.trim();
  return /^(https?:|mailto:|tel:|#|\/|\.\.?\/)/i.test(url)
    || /^[a-z0-9][a-z0-9_./-]*$/i.test(url)
    ? url
    : '#';
};

// Author-only notes are embedded as HTML comments (e.g. `<!-- CAPTURA: ... -->`).
// The lightweight Markdown renderer below has no comment branch, so a stray
// comment would otherwise render as literal paragraph text on the page. Strip
// every HTML comment (single- or multi-line) from the raw manual before parsing.
export const stripHtmlComments = (markdown = '') => markdown.replace(/<!--[\s\S]*?-->/g, '');

export const parseSections = (rawMarkdown) => {
  const markdown = stripHtmlComments(rawMarkdown);
  const sections = [];
  let current = { id: 'introduccion', heading: '', lines: [] };

  markdown.split(/\r?\n/).forEach((line) => {
    const match = line.match(/^##\s+(.+)$/);
    if (match) {
      if (current.lines.some((item) => item.trim())) {
        sections.push({ ...current, content: current.lines.join('\n') });
      }
      current = { id: slugify(match[1]), heading: match[1].trim(), lines: [] };
    } else {
      current.lines.push(line);
    }
  });

  if (current.lines.some((item) => item.trim())) {
    sections.push({ ...current, content: current.lines.join('\n') });
  }

  return sections.map(({ lines, ...section }, index) => ({
    ...section,
    content: index === 0
      ? section.content.replace(/^#\s+.*(?:\r?\n|$)/, '').trim()
      : section.content.trim(),
  }));
};

const highlightText = (text, query, keyPrefix) => {
  const normalizedQuery = foldText(query.trim());
  if (!normalizedQuery) return text;

  const folded = foldText(text);
  const nodes = [];
  let cursor = 0;
  let match = folded.indexOf(normalizedQuery);

  while (match !== -1) {
    if (match > cursor) nodes.push(text.slice(cursor, match));
    nodes.push(
      <mark key={`${keyPrefix}-${match}`} className="rounded bg-yellow-200 px-0.5 text-gray-900 dark:bg-yellow-400/80">
        {text.slice(match, match + normalizedQuery.length)}
      </mark>
    );
    cursor = match + normalizedQuery.length;
    match = folded.indexOf(normalizedQuery, cursor);
  }

  if (!nodes.length) return text;
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
};

const renderInline = (text, query, keyPrefix) => {
  const tokens = /(!?\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g;
  const nodes = [];
  let cursor = 0;
  let match;

  while ((match = tokens.exec(text)) !== null) {
    if (match.index > cursor) {
      nodes.push(
        <React.Fragment key={`${keyPrefix}-text-${cursor}`}>
          {highlightText(text.slice(cursor, match.index), query, `${keyPrefix}-${cursor}`)}
        </React.Fragment>
      );
    }

    const token = match[0];
    const link = token.match(/^(!?)\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const url = safeUrl(link[3]);
      if (link[1]) {
        nodes.push(<img key={`${keyPrefix}-${match.index}`} src={url} alt={link[2]} />);
      } else {
        const external = /^https?:/i.test(url);
        nodes.push(
          <a
            key={`${keyPrefix}-${match.index}`}
            href={url}
            className="font-medium text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-700 dark:text-blue-400"
            {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
          >
            {highlightText(link[2], query, `${keyPrefix}-link-${match.index}`)}
          </a>
        );
      }
    } else if (token.startsWith('**')) {
      nodes.push(<strong key={`${keyPrefix}-${match.index}`} className="font-semibold">{highlightText(token.slice(2, -2), query, `${keyPrefix}-strong-${match.index}`)}</strong>);
    } else if (token.startsWith('`')) {
      nodes.push(<code key={`${keyPrefix}-${match.index}`} className="rounded bg-gray-100 px-1.5 py-0.5 text-[0.9em] text-blue-700 dark:bg-white/10 dark:text-blue-300">{highlightText(token.slice(1, -1), query, `${keyPrefix}-code-${match.index}`)}</code>);
    } else {
      nodes.push(<em key={`${keyPrefix}-${match.index}`}>{highlightText(token.slice(1, -1), query, `${keyPrefix}-em-${match.index}`)}</em>);
    }
    cursor = match.index + token.length;
  }

  if (cursor < text.length) {
    nodes.push(
      <React.Fragment key={`${keyPrefix}-text-${cursor}`}>
        {highlightText(text.slice(cursor), query, `${keyPrefix}-${cursor}`)}
      </React.Fragment>
    );
  }
  return nodes;
};

const splitTableRow = (line) => line.trim().replace(/^\||\|$/g, '').split('|').map((cell) => cell.trim());

const startsBlock = (lines, index) => {
  const line = lines[index] || '';
  const next = lines[index + 1] || '';
  return /^#{1,4}\s+/.test(line)
    || /^\s*([-*+]\s+|\d+\.\s+)/.test(line)
    || /^>\s?/.test(line)
    || /^```/.test(line)
    || /^---+$/.test(line.trim())
    || /^!\[[^\]]+\]\([^)]+\)$/.test(line.trim())
    || (line.includes('|') && /^\s*\|?\s*:?-{3,}/.test(next));
};

function MarkdownContent({ content, query, imageUnavailableLabel }) {
  const lines = content.split(/\r?\n/);
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    // Defensive: never render HTML comments as text. Skip a comment that occupies
    // whole lines (including a multi-line comment) so author notes stay hidden.
    if (/^\s*<!--/.test(line)) {
      while (index < lines.length && !/-->/.test(lines[index])) {
        index += 1;
      }
      index += 1;
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      const Heading = heading[1].length <= 2 ? 'h3' : 'h4';
      blocks.push(
        <Heading key={`heading-${index}`} className={Heading === 'h3' ? 'mb-2 mt-8 text-xl font-semibold' : 'mb-2 mt-6 text-base font-semibold'}>
          {renderInline(heading[2], query, `heading-${index}`)}
        </Heading>
      );
      index += 1;
      continue;
    }

    if (/^---+$/.test(line.trim())) {
      blocks.push(<hr key={`rule-${index}`} className="my-8 border-border" />);
      index += 1;
      continue;
    }

    if (/^```/.test(line.trim())) {
      const code = [];
      index += 1;
      while (index < lines.length && !/^```/.test(lines[index].trim())) {
        code.push(lines[index]);
        index += 1;
      }
      index += 1;
      blocks.push(<pre key={`code-${index}`} className="my-5 overflow-x-auto rounded-lg bg-gray-950 p-4 text-sm text-gray-100"><code>{code.join('\n')}</code></pre>);
      continue;
    }

    const image = line.trim().match(/^!\[([^\]]+)\]\(([^)]+)\)$/);
    if (image) {
      blocks.push(
        <figure key={`image-${index}`} className="my-6 overflow-hidden rounded-xl border border-border bg-background">
          <img
            src={safeUrl(image[2])}
            alt={image[1]}
            className="max-h-[32rem] w-full object-contain"
            onError={(event) => {
              event.currentTarget.style.display = 'none';
              event.currentTarget.nextElementSibling.style.display = 'flex';
            }}
          />
          <div className="hidden min-h-28 items-center justify-center px-6 text-center text-sm" style={{ color: 'var(--muted)' }}>
            {imageUnavailableLabel}: {image[1]}
          </div>
        </figure>
      );
      index += 1;
      continue;
    }

    if (line.includes('|') && /^\s*\|?\s*:?-{3,}/.test(lines[index + 1] || '')) {
      const headers = splitTableRow(line);
      const rows = [];
      index += 2;
      while (index < lines.length && lines[index].includes('|') && lines[index].trim()) {
        rows.push(splitTableRow(lines[index]));
        index += 1;
      }
      const tableKey = `table-${index}`;
      blocks.push(
        <div key={tableKey} className="my-6 overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full divide-y divide-border text-left text-sm">
            <thead className="bg-gray-50 dark:bg-white/5"><tr>{headers.map((cell, cellIndex) => <th key={cellIndex} className="px-4 py-3 font-semibold">{renderInline(cell, query, `${tableKey}-th-${cellIndex}`)}</th>)}</tr></thead>
            <tbody className="divide-y divide-border">{rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex} className="px-4 py-3 align-top">{renderInline(cell, query, `${tableKey}-td-${rowIndex}-${cellIndex}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }

    const list = line.match(/^\s*([-*+]|\d+\.)\s+(.+)$/);
    if (list) {
      const ordered = /\d+\./.test(list[1]);
      const items = [];
      while (index < lines.length) {
        const item = lines[index].match(/^\s*([-*+]|\d+\.)\s+(.+)$/);
        if (!item || /\d+\./.test(item[1]) !== ordered) break;
        items.push(item[2]);
        index += 1;
      }
      const List = ordered ? 'ol' : 'ul';
      const listKey = `list-${index}`;
      blocks.push(<List key={listKey} className={`my-4 space-y-2 pl-6 ${ordered ? 'list-decimal' : 'list-disc'}`}>{items.map((item, itemIndex) => <li key={itemIndex} className="pl-1">{renderInline(item, query, `${listKey}-item-${itemIndex}`)}</li>)}</List>);
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quote = [];
      while (index < lines.length && /^>\s?/.test(lines[index])) {
        quote.push(lines[index].replace(/^>\s?/, ''));
        index += 1;
      }
      blocks.push(<blockquote key={`quote-${index}`} className="my-5 rounded-r-lg border-l-4 border-blue-500 bg-blue-50 px-5 py-4 text-blue-950 dark:bg-blue-500/10 dark:text-blue-100">{renderInline(quote.join(' '), query, `quote-${index}`)}</blockquote>);
      continue;
    }

    const paragraph = [line.trim()];
    index += 1;
    while (index < lines.length && lines[index].trim() && !startsBlock(lines, index)) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    blocks.push(<p key={`paragraph-${index}`} className="my-4 leading-7">{renderInline(paragraph.join(' '), query, `paragraph-${index}`)}</p>);
  }

  return blocks;
}

function Docs() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [activeSection, setActiveSection] = useState('');
  const sections = useMemo(() => parseSections(manual), []);
  const toc = useMemo(() => sections.filter((section) => section.heading), [sections]);
  const filteredSections = useMemo(() => {
    const normalizedQuery = foldText(query.trim());
    return normalizedQuery
      ? sections.filter((section) => foldText(`${section.heading}\n${section.content}`).includes(normalizedQuery))
      : sections;
  }, [query, sections]);

  useEffect(() => {
    const updateFromHash = () => setActiveSection(window.location.hash.slice(1));
    updateFromHash();
    window.addEventListener('hashchange', updateFromHash);
    return () => window.removeEventListener('hashchange', updateFromHash);
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <header className="mb-8 border-b border-border pb-7">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400"><BookOpenIcon className="h-5 w-5" />{t('docs.eyebrow')}</div>
        <h1 className="text-2xl font-bold leading-7 text-foreground sm:text-3xl sm:truncate">{t('docs.title')}</h1>
        <p className="mt-3 max-w-3xl text-sm leading-6 sm:text-base" style={{ color: 'var(--muted)' }}>{t('docs.description')}</p>
        <div className="relative mt-6 max-w-2xl">
          <label htmlFor="docs-search" className="sr-only">{t('docs.searchLabel')}</label>
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2" style={{ color: 'var(--muted)' }} />
          <input id="docs-search" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('docs.searchPlaceholder')} className="input-field w-full py-3 pl-11 pr-11" />
          {query && <button type="button" onClick={() => setQuery('')} aria-label={t('docs.clearSearch')} className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1.5 hover:bg-gray-100 dark:hover:bg-white/10"><XMarkIcon className="h-4 w-4" /></button>}
        </div>
        {query && <p className="mt-2 text-sm" aria-live="polite" style={{ color: 'var(--muted)' }}>{t('docs.results', { count: filteredSections.length })}</p>}
      </header>

      <div className="grid gap-8 lg:grid-cols-[15rem_minmax(0,1fr)] lg:items-start">
        <aside className="lg:sticky lg:top-6">
          <div className="rounded-xl border border-border bg-card p-4">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>{t('docs.contents')}</h2>
            <nav aria-label={t('docs.contents')} className="max-h-64 overflow-y-auto pr-1 lg:max-h-[calc(100vh-9rem)]">
              <ul className="space-y-0.5">{toc.map((section) => <li key={section.id}><a href={`#${section.id}`} onClick={() => setActiveSection(section.id)} className={`block rounded-lg px-3 py-2 text-sm leading-5 transition-colors ${activeSection === section.id ? 'bg-blue-50 font-medium text-blue-700 dark:bg-blue-500/10 dark:text-blue-300' : 'hover:bg-gray-50 dark:hover:bg-white/5'}`} style={activeSection === section.id ? undefined : { color: 'var(--muted)' }}>{section.heading}</a></li>)}</ul>
            </nav>
          </div>
        </aside>

        <main className="min-w-0">
          {filteredSections.length ? filteredSections.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-6 border-b border-border pb-10 pt-1 first:pt-0 last:border-b-0">
              {section.heading && <h2 className="mb-5 mt-7 text-2xl font-bold tracking-tight first:mt-0 sm:text-[1.7rem]">{renderInline(section.heading, query, `section-${section.id}`)}</h2>}
              <div className="text-[0.95rem]"><MarkdownContent content={section.content} query={query} imageUnavailableLabel={t('docs.imageUnavailable')} /></div>
            </section>
          )) : (
            <div className="rounded-xl border border-dashed border-border px-6 py-16 text-center">
              <MagnifyingGlassIcon className="mx-auto h-9 w-9" style={{ color: 'var(--muted)' }} />
              <h2 className="mt-4 text-lg font-semibold">{t('docs.noResultsTitle')}</h2>
              <p className="mt-2 text-sm" style={{ color: 'var(--muted)' }}>{t('docs.noResultsDescription')}</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default Docs;
